import { query, Not, hasComponent } from 'bitecs';
import { DragonTag, Enemy, EnemyKind, FxTag, Orbiter, Pickup, Position, PrevPosition, Projectile, Radius, Velocity } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { RoomPhase, type GameContext } from '@src/ecs/world';
import { BOUNDS } from '@src/game/arena/RoomLayout';

const PICKUP_DRAG = 4.5;

/** Integrates velocity and keeps bodies (not projectiles, FX or the dragon) inside the room. */
export class MovementSystem implements GameSystem {
  private ctx!: GameContext;

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const world = this.ctx.world;

    // Free movers: projectiles fly off-screen and get culled, the dragon ignores walls.
    const free = query(world, [Position, Velocity, Projectile]);
    for (let i = 0; i < free.length; i++) {
      const eid = free[i];
      PrevPosition.x[eid] = Position.x[eid];
      PrevPosition.y[eid] = Position.y[eid];
      Position.x[eid] += Velocity.x[eid] * dt;
      Position.y[eid] += Velocity.y[eid] * dt;
    }
    const dragons = query(world, [Position, Velocity, DragonTag]);
    for (let i = 0; i < dragons.length; i++) {
      const eid = dragons[i];
      PrevPosition.x[eid] = Position.x[eid];
      PrevPosition.y[eid] = Position.y[eid];
      Position.x[eid] += Velocity.x[eid] * dt;
      Position.y[eid] += Velocity.y[eid] * dt;
    }

    const bodies = query(world, [Position, Velocity, Radius, Not(Projectile), Not(DragonTag), Not(FxTag), Not(Orbiter)]);
    const freeWalk = this.ctx.room.phase === RoomPhase.Transition;
    for (let i = 0; i < bodies.length; i++) {
      const eid = bodies[i];
      if (hasComponent(world, eid, Enemy) && (Enemy.kind[eid] === EnemyKind.Gargoyle || Enemy.kind[eid] === EnemyKind.Spitter)) {
        PrevPosition.x[eid] = Position.x[eid];
        PrevPosition.y[eid] = Position.y[eid];
        Velocity.x[eid] = 0;
        Velocity.y[eid] = 0;
        continue;
      }
      PrevPosition.x[eid] = Position.x[eid];
      PrevPosition.y[eid] = Position.y[eid];
      let x = Position.x[eid] + Velocity.x[eid] * dt;
      let y = Position.y[eid] + Velocity.y[eid] * dt;
      const r = Radius.r[eid];
      if (!freeWalk && x < BOUNDS.minX + r) {
        x = BOUNDS.minX + r;
        if (Velocity.x[eid] < 0) Velocity.x[eid] = 0;
      } else if (!freeWalk && x > BOUNDS.maxX - r) {
        x = BOUNDS.maxX - r;
        if (Velocity.x[eid] > 0) Velocity.x[eid] = 0;
      }
      if (!freeWalk && y < BOUNDS.minY + r) {
        y = BOUNDS.minY + r;
        if (Velocity.y[eid] < 0) Velocity.y[eid] = 0;
      } else if (!freeWalk && y > BOUNDS.maxY - r) {
        y = BOUNDS.maxY - r;
        if (Velocity.y[eid] > 0) Velocity.y[eid] = 0;
      }
      Position.x[eid] = x;
      Position.y[eid] = y;
    }

    // FX sprites stay put (PrevPosition must still track so interpolation is stable).
    const fx = query(world, [Position, FxTag]);
    for (let i = 0; i < fx.length; i++) {
      const eid = fx[i];
      PrevPosition.x[eid] = Position.x[eid];
      PrevPosition.y[eid] = Position.y[eid];
    }

    // Gems and lobbed prizes slide to a stop; supply drops glide until their drift runs out.
    const pickups = query(world, [Pickup, Velocity]);
    const drag = Math.exp(-PICKUP_DRAG * dt);
    for (let i = 0; i < pickups.length; i++) {
      const eid = pickups[i];
      if (Pickup.drift[eid] > 0) {
        Pickup.drift[eid] -= dt;
        continue;
      }
      Velocity.x[eid] *= drag;
      Velocity.y[eid] *= drag;
    }
  }
}
